import * as React from 'react';
import { useMemo } from 'react';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { CommunicationQuotas, CommunicationChannel, RemindersSettingsData } from '../../../models';
import Typography from '../../../components/Typography';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { PlaceholderTextField, TextInputField } from '../../../components/Forms/fields';
import { NumberInputField } from '../../../components/Forms/fields';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import { paths } from '../../../app/constants';
import {
    ChannelCard, ChannelHeader, ChannelInfo, ChannelContent, ChannelStatus, ChannelQuota,
    ChannelWarning, MySubscriptionLink, getMessageIcon,
    normalizeClientsFollowUpSettings,
} from './remindersShared';

export interface FollowUpTabProps {
    values: RemindersSettingsData;
    onChange: (reminders: RemindersSettingsData) => void;
    communicationQuotas: CommunicationQuotas | null;
}

const FollowUpTab: React.FC<FollowUpTabProps> = ({ values, onChange, communicationQuotas }) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();
    const culture = getLanguage();
    const privacyPolicyUrl = `https://appointmint.pt/${culture}/privacy`;
    const [whatsAppHelpContext, setWhatsAppHelpContext] = React.useState<'client' | null>(null);

    const getTranslationWithFallback = React.useCallback((key: TK, fallback: string) => {
        const translated = t(key);
        return translated === key ? fallback : translated;
    }, [t]);

    const whatsAppDisclaimerStart = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerStart,
        'Appointmint uses its own phone number'
    );
    const whatsAppDisclaimerEnd = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerEnd,
        'to communicate with your clients by WhatsApp, send reminders and confirmations, receive responses, and all communications are visible in the client profile communication chat.'
    );

    const companyName = generalSettings.companyName ?? '';

    const followUpCommunicationDefaultTemplate = useMemo(() =>
        getTranslationWithFallback(
            TK.clientsFollowUpCommunicationDefaultTemplate,
            `Olá {client-name},\n\nEsperamos que a sua primeira consulta tenha sido uma boa experiência.\nSe surgir alguma dúvida basta responder a esta mensagem ou ligar-nos.\n\nCumprimentos da equipa {companyName}.\nObrigado!`
        ).replace(/{companyName}/g, companyName),
    [getTranslationWithFallback, companyName]);

    const clientsFollowUp = useMemo(() => normalizeClientsFollowUpSettings(values), [values]);
    const followUpCommunicationEnabled = Boolean(
        clientsFollowUp.communicationChannel !== undefined && clientsFollowUp.communicationTemplate.trim()
    );

    const buildUsageLabel = React.useCallback((used?: number, allowed?: number) =>
        used !== undefined && allowed !== undefined ? `${Math.min(used, allowed)}/${allowed}` : null
    , []);

    const smsUsageLabel = buildUsageLabel(communicationQuotas?.smsUsed, communicationQuotas?.smsAllowed);
    const whatsAppUsed = communicationQuotas?.whatsAppUsed;
    const whatsAppAllowed = communicationQuotas?.whatsAppAllowed;
    const whatsAppUsageLabel = buildUsageLabel(whatsAppUsed, whatsAppAllowed);
    const whatsAppQuotaExhausted = whatsAppUsed !== undefined
        && whatsAppAllowed !== undefined
        && whatsAppUsed >= whatsAppAllowed;

    const getReminderChannelUsageLabel = React.useCallback((channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') return smsUsageLabel;
        if (channel === 'email') return null;
        return whatsAppUsageLabel;
    }, [smsUsageLabel, whatsAppUsageLabel]);

    const onChangeReminders = (reminders: RemindersSettingsData) => onChange(reminders);

    const updateFollowUpCommunicationChannel = (channel: CommunicationChannel) => {
        onChangeReminders({
            ...values,
            clientsFollowUp: {
                ...(values.clientsFollowUp ?? {}),
                enabled: clientsFollowUp.enabled,
                daysAfterFirstAppointment: clientsFollowUp.daysAfterFirstAppointment,
                communicationTemplate: values.clientsFollowUp?.communicationTemplate?.trim() || followUpCommunicationDefaultTemplate,
                communicationChannel: channel,
                communicationSubjectTemplate: values.clientsFollowUp?.communicationSubjectTemplate,
            },
        });
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <Typography size="large"><b>{t(TK.automaticFollowUp)}</b></Typography>

            <CheckInputField
                id='clients-follow-up-automatic-enabled'
                label={t(TK.sendAutomaticFollowUpCommunication)}
                value={clientsFollowUp.enabled}
                onChange={enabled => onChangeReminders({
                    ...values,
                    clientsFollowUp: {
                        ...(values.clientsFollowUp ?? {}),
                        enabled,
                        daysAfterFirstAppointment: Math.max(1, values.clientsFollowUp?.daysAfterFirstAppointment ?? 1),
                        communicationChannel: values.clientsFollowUp?.communicationChannel ?? CommunicationChannel.Email,
                        communicationTemplate: values.clientsFollowUp?.communicationTemplate?.trim() || followUpCommunicationDefaultTemplate,
                        communicationSubjectTemplate: values.clientsFollowUp?.communicationSubjectTemplate,
                    },
                })}
            />

            {clientsFollowUp.enabled && (
                <>
                    <NumberInputField
                        prepend={t(TK.daysAfterFirstAppointment)}
                        append={t(TK.days)}
                        value={clientsFollowUp.daysAfterFirstAppointment}
                        onChange={daysAfterFirstAppointment => onChangeReminders({
                            ...values,
                            clientsFollowUp: {
                                ...(values.clientsFollowUp ?? {}),
                                enabled: clientsFollowUp.enabled,
                                daysAfterFirstAppointment: Math.max(1, daysAfterFirstAppointment ?? 1),
                                communicationChannel: values.clientsFollowUp?.communicationChannel ?? CommunicationChannel.Email,
                                communicationTemplate: values.clientsFollowUp?.communicationTemplate?.trim() || followUpCommunicationDefaultTemplate,
                                communicationSubjectTemplate: values.clientsFollowUp?.communicationSubjectTemplate,
                            },
                        })}
                    />

                    {followUpCommunicationEnabled && (
                        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                            <Typography size="large"><b>{t(TK.followUpCommunicationChannel)}</b></Typography>

                            {[CommunicationChannel.Email, CommunicationChannel.SMS, CommunicationChannel.WhatsApp].map(channel => {
                                const isSelected = clientsFollowUp.communicationChannel === channel;
                                const isWhatsApp = channel === CommunicationChannel.WhatsApp;
                                const isWhatsAppCheckboxDisabled = isWhatsApp && whatsAppQuotaExhausted && !isSelected;
                                const channelUsageLabel = getReminderChannelUsageLabel(
                                    channel === CommunicationChannel.Email ? 'email' : channel === CommunicationChannel.SMS ? 'sms' : 'whatsapp'
                                );

                                return (
                                    <ChannelCard key={channel} $isActive={isSelected} $isSelected={isSelected}>
                                        <ChannelHeader
                                            $isActive={isSelected}
                                            $isSelected={isSelected}
                                            onClick={() => {
                                                if (isSelected || isWhatsAppCheckboxDisabled) return;
                                                updateFollowUpCommunicationChannel(channel);
                                            }}
                                        >
                                            <CheckInputField
                                                id={`clients-follow-up-channel-${channel}`}
                                                label=""
                                                value={isSelected}
                                                disabled={isWhatsAppCheckboxDisabled}
                                                onChange={selected => {
                                                    if (!selected || isWhatsAppCheckboxDisabled) return;
                                                    updateFollowUpCommunicationChannel(channel);
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <ChannelInfo>
                                                {channel === CommunicationChannel.Email && getMessageIcon('email')}
                                                {channel === CommunicationChannel.SMS && getMessageIcon('sms')}
                                                {channel === CommunicationChannel.WhatsApp && getMessageIcon('whatsapp')}
                                                <Typography>
                                                    <b>
                                                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexWrap: 'wrap'}}>
                                                            <span>{channel === CommunicationChannel.Email ? t(TK.email) : channel === CommunicationChannel.SMS ? t(TK.sms) : 'WhatsApp'}</span>
                                                            {channelUsageLabel && <ChannelQuota>({channelUsageLabel})</ChannelQuota>}
                                                            {isWhatsApp && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                                        e.stopPropagation();
                                                                        setWhatsAppHelpContext('client');
                                                                    }}
                                                                    style={{padding: 2}}
                                                                >
                                                                    <HelpIcon style={{fontSize: '1rem'}} />
                                                                </IconButton>
                                                            )}
                                                        </span>
                                                    </b>
                                                </Typography>
                                                <ChannelStatus $isActive={isSelected}>
                                                    {isSelected ? t(TK.active) : t(TK.inactive)}
                                                </ChannelStatus>
                                                {isWhatsApp && isWhatsAppCheckboxDisabled && (
                                                    <ChannelWarning onClick={(event: React.MouseEvent<HTMLSpanElement>) => event.stopPropagation()}>
                                                        {t(TK.noWhatsAppQuotaAvailableAddMoreIn)}{' '}
                                                        <MySubscriptionLink to={paths.mySubscription()} onClick={event => event.stopPropagation()}>
                                                            {t(TK.mySubscription)}
                                                        </MySubscriptionLink>
                                                    </ChannelWarning>
                                                )}
                                            </ChannelInfo>
                                        </ChannelHeader>

                                        {isSelected && (
                                            <ChannelContent>
                                                {channel === CommunicationChannel.Email && (
                                                    <TextInputField
                                                        label={t(TK.followUpCommunicationSubjectTemplate)}
                                                        maxLength={500}
                                                        value={values.clientsFollowUp?.communicationSubjectTemplate ?? ''}
                                                        onChange={communicationSubjectTemplate => onChangeReminders({
                                                            ...values,
                                                            clientsFollowUp: {
                                                                ...(values.clientsFollowUp ?? {}),
                                                                enabled: clientsFollowUp.enabled,
                                                                daysAfterFirstAppointment: clientsFollowUp.daysAfterFirstAppointment,
                                                                communicationChannel: values.clientsFollowUp?.communicationChannel ?? CommunicationChannel.Email,
                                                                communicationTemplate: values.clientsFollowUp?.communicationTemplate?.trim() || followUpCommunicationDefaultTemplate,
                                                                communicationSubjectTemplate,
                                                            },
                                                        })}
                                                    />
                                                )}

                                                {channel === CommunicationChannel.WhatsApp && (
                                                    <Typography size="smaller" style={{color: 'var(--text-muted)'}}>
                                                        {t(TK.whatsAppTemplateMetaWarning)}
                                                    </Typography>
                                                )}
                                                <PlaceholderTextField
                                                    id={`clients-follow-up-channel-${channel}-template`}
                                                    label={t(TK.followUpCommunicationTemplate)}
                                                    type='textarea'
                                                    maxLength={10000}
                                                    disabled={channel === CommunicationChannel.WhatsApp}
                                                    value={values.clientsFollowUp?.communicationTemplate ?? followUpCommunicationDefaultTemplate}
                                                    onChange={communicationTemplate => onChangeReminders({
                                                        ...values,
                                                        clientsFollowUp: {
                                                            ...(values.clientsFollowUp ?? {}),
                                                            enabled: clientsFollowUp.enabled,
                                                            daysAfterFirstAppointment: clientsFollowUp.daysAfterFirstAppointment,
                                                            communicationChannel: values.clientsFollowUp?.communicationChannel ?? CommunicationChannel.Email,
                                                            communicationTemplate,
                                                            communicationSubjectTemplate: values.clientsFollowUp?.communicationSubjectTemplate,
                                                        },
                                                    })}
                                                />
                                            </ChannelContent>
                                        )}
                                    </ChannelCard>
                                );
                            })}
                        </div>
                    )}

                </>
            )}

            <Dialog open={whatsAppHelpContext !== null} onClose={() => setWhatsAppHelpContext(null)}>
                <DialogTitle>{t(TK.automaticFollowUp)}</DialogTitle>
                <DialogContent>
                    <div>
                        {whatsAppDisclaimerStart} (+351936385141) {whatsAppDisclaimerEnd}
                    </div>
                    <div style={{marginTop: 'var(--spacer-XS)'}}>
                        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">{t(TK.privacyPolicy)}</a>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FollowUpTab;
